import type { SVGProps } from "react";

interface SVGRProps {
  title?: string;
  titleId?: string;
  desc?: string;
  descId?: string;
  width?: number;
}

const GRADIENT_START = "#06c9ff";
const GRADIENT_END = "#ad44ff";

// The one part of this mark that follows the theme: "earlyup" and the "e"
// icon's plain (non-gradient) shape are black on light, white on dark — the
// exact two-file split the approved earlyup-pro-black/white.svg assets draw,
// done here as one markup instead of swapping files. `dark:` keys off the
// `.dark` class next-themes puts on <html> (or a forced ancestor, e.g. a
// story pinning one rendering), so no `variant` prop is needed — the caller
// never has to tell this component which theme it's in.
const THEME_FILL = "fill-black dark:fill-white";

/**
 * The full "earlyup Pro" wordmark — icon, "earlyup" and "Pro" — as one
 * image, byte-matched against the approved `earlyup-pro-black.svg` /
 * `earlyup-pro-white.svg` brand assets (`apps/marketing-professional/public/
 * brand/`). The name is complete here: no caller should render adjacent
 * "Pro" text to finish it.
 *
 * The icon's own two-stop gradient and "Pro"'s three-stop accent gradient
 * are fixed brand colours in both source files — untouched by theme.
 */
export default function LogoWordmark({
  title = "Earlyup Pro",
  titleId,
  desc = "Earlyup - Connecting People, Empowering Lives",
  descId,
  ...props
}: SVGProps<SVGSVGElement> & SVGRProps) {
  return (
    <svg
      id="ergon-logo-wordmark"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 980.84 174.19"
      shapeRendering="geometricPrecision"
      textRendering="optimizeLegibility"
      aria-labelledby={titleId}
      aria-describedby={descId}
      {...props}
    >
      {desc ? <desc id={descId}>{desc}</desc> : null}
      {title ? <title id={titleId}>{title}</title> : null}

      <defs>
        <linearGradient
          id="ergon-wordmark-gradient-1"
          x1="-321.39"
          y1="522.29"
          x2="-431.85"
          y2="392.8"
          gradientTransform="translate(444 530.19) scale(1 -1)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={GRADIENT_START} />
          <stop offset="65%" stopColor={GRADIENT_END} />
        </linearGradient>

        <linearGradient
          id="ergon-wordmark-gradient-2"
          x1="-307.77"
          y1="510.67"
          x2="-418.23"
          y2="381.18"
          gradientTransform="translate(444 530.19) scale(1 -1)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={GRADIENT_START} />
          <stop offset="65%" stopColor={GRADIENT_END} />
        </linearGradient>

        <linearGradient
          id="ergon-wordmark-gradient-3"
          x1="-257.73"
          y1="467.98"
          x2="-368.19"
          y2="338.5"
          gradientTransform="translate(444 530.19) scale(1 -1)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={GRADIENT_START} />
          <stop offset="65%" stopColor={GRADIENT_END} />
        </linearGradient>

        {/* "Pro"'s own fixed accent gradient — same in both themes */}
        <linearGradient
          id="ergon-wordmark-pro-gradient"
          x1="759.34"
          y1="130.07"
          x2="954.99"
          y2="17.11"
          gradientTransform="translate(-6.79 -6.36) rotate(.36)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="12%" stopColor="#ac44ff" />
          <stop offset="48%" stopColor="#6979ff" />
          <stop offset="91%" stopColor="#1db6ff" />
        </linearGradient>
      </defs>

      <g id="ergon-logo-wordmark-group">
        {/* The "e" symbol's plain shape — same shape as the standalone Logo */}
        <path
          d="M60.82,77.1c2.61-11.29,10.24-19.97,23.44-19.97,12.34,0,19.8,8.68,21.71,19.97h-45.15ZM84.26,112.7c-12.34,0-21.53-8.51-23.44-20.83h65.98l.35-2.43c.22-1.56.52-3.64.52-6.25,0-25.19-19.1-44.29-43.41-44.29-26.05,0-45.15,19.1-45.15,46.02s19.1,46.02,45.15,46.02h23.76v-18.24h-23.76Z"
          fillRule="evenodd"
          className={THEME_FILL}
        />

        {/* Left circular element */}
        <path
          d="M34.88,42.81c8.46,0,15.33-6.86,15.33-15.33s-6.86-15.33-15.33-15.33-15.33,6.86-15.33,15.33c0,.88.07,1.75.22,2.59C7.44,44.79,0,63.8,0,84.56c0,44.16,33.64,80.41,76.54,84.23-1.49-2.99-2.34-6.36-2.34-9.92,0-3.29.72-6.41,2-9.21-32.29-3.89-57.32-31.55-57.32-65.09,0-15.88,5.61-30.44,14.95-41.79.35.02.7.04,1.06.04Z"
          fill="url(#ergon-wordmark-gradient-1)"
        />

        {/* Top circular element */}
        <path
          d="M158.34,100.2c8.46,0,15.33-6.86,15.33-15.33,0-5.16-2.55-9.73-6.47-12.51C161.32,31.44,126.33,0,84.03,0c-12.94,0-25.2,2.94-36.15,8.2,5.4,3.65,9.18,9.5,10.05,16.26,7.99-3.52,16.81-5.47,26.09-5.47,32.19,0,58.92,23.48,64.21,54.35-3.2,2.81-5.23,6.93-5.23,11.53,0,8.46,6.86,15.33,15.33,15.33Z"
          fill="url(#ergon-wordmark-gradient-2)"
        />

        {/* Bottom circular element */}
        <path
          d="M81.07,158.87c0,8.46,6.86,15.33,15.33,15.33,6.35,0,11.68-3.83,14.01-9.32,26.88-8.94,47.75-31.15,54.94-58.93-2.2.73-4.56,1.13-7.01,1.13-4.4,0-8.49-1.28-11.94-3.48-6.04,20.06-21.37,36.05-40.95,42.91-2.54-1.86-5.66-2.96-9.05-2.96-8.46,0-15.33,6.86-15.33,15.33Z"
          fill="url(#ergon-wordmark-gradient-3)"
        />

        {/* "earlyup" lettering */}
        <path
          d="M508.85,99.09h3l18.01-51.76h19.51l-32.26,91.52c-3.6,10.5-8.4,15-22.51,15h-15.75v-17.25h15c3.75,0,4.95-1.5,5.7-3.75l1.35-3.75-31.06-81.77h19.51l19.5,51.76ZM685.08,45.83c19.51,0,35.26,15.75,35.26,39.76s-15.75,39.76-35.26,39.76c-7.95,0-13.35-2.25-16.8-4.5-1.95-1.35-3.6-2.85-4.95-4.5h-.75v37.51h-18.76V47.33h17.25l1.5,8.25h.75c1.35-1.8,3.15-3.45,5.25-4.8,3.6-2.4,8.85-4.95,16.5-4.95ZM682.08,63.08c-11.4,0-19.5,8.1-19.5,22.51s8.1,22.51,19.5,22.51,19.5-8.1,19.5-22.51-8.1-22.51-19.5-22.51ZM335.18,45.83c19.5,0,32.26,12.9,32.26,29.26v48.76h-15.75l-1.5-7.5h-.75c-1.35,1.65-3.15,3.15-5.25,4.5-3.6,2.25-8.85,4.5-16.5,4.5-17.4,0-27.76-10.65-27.76-23.26,0-15,10.5-25.51,31.51-25.51h17.25v-1.5c0-8.4-5.1-14.25-13.5-14.25-8.4,0-12.75,5.4-13.5,9.75h-18.75c1.65-12.6,11.85-24.76,32.26-24.76ZM332.92,90.84c-9.75,0-14.25,3.75-14.25,9.75s4.35,9.75,12,9.75c11.4,0,18-6.6,18-17.25v-2.25h-15.75ZM580.09,91.59c0,9.75,6.75,16.5,16.5,16.5,9.75,0,16.5-6.75,16.5-16.5v-44.26h18.75v76.52h-17.25l-1.5-7.5h-.75c-1.35,1.65-3.15,3.15-5.1,4.5-3.45,2.25-8.85,4.5-15.9,4.5-17.26,0-30.01-12.75-30.01-32.26v-45.76h18.76v44.26ZM253.41,45.82c21,0,37.49,16.49,37.49,38.24,0,2.26-.25,4.05-.45,5.4l-.3,2.09h-56.99c1.65,10.64,9.61,17.99,20.25,17.99h20.51v15.74h-20.51c-22.5,0-38.99-16.49-38.99-39.73,0-23.24,16.5-39.73,38.99-39.73ZM253.41,61.56c-11.4,0-18.01,7.5-20.25,17.24h39c-1.66-9.74-8.11-17.24-18.75-17.24ZM399.41,54.08h.75c.9-1.2,1.95-2.25,3.3-3.3,2.25-1.65,5.55-3.45,10.2-3.45h15v18h-13.5c-9.45,0-14.25,4.8-14.25,14.25v44.26h-18.75V47.33h15.75l1.5,6.75ZM459.39,123.85h-18.75V18.82h18.75v105.03Z"
          fillRule="evenodd"
          className={THEME_FILL}
        />

        {/* "Pro" — the accent gradient, fixed in both themes */}
        <path
          d="M849.14,123.62l18.75.12.28-44.24c.06-9.44,4.9-14.22,14.34-14.16l13.5.08.11-17.99-15-.09c-4.64-.03-7.97,1.73-10.21,3.38-1.37,1.01-2.41,2.08-3.31,3.27h-.75s-1.45-6.76-1.45-6.76l-15.74-.1-.48,76.47-.04.02ZM749.28,18.11l52.65.32c44.1,3.43,43.94,67.03-.2,70.18l-33.46-.22-.22,34.72-19.42-.12.66-104.88ZM768.38,69.71l30.35.2c21.54-.6,22.85-30.78.44-32.35l-30.59-.18-.2,32.33ZM942.1,46.22c-22.49-.14-39.09,16.25-39.23,39.49s16.25,39.84,38.74,39.98c22.49.14,39.09-16.25,39.23-39.49s-16.25-39.84-38.74-39.98ZM941.71,108.46c-11.26-.07-20.19-9.12-20.11-22.62.08-13.5,9.13-22.44,20.39-22.37,11.26.07,20.19,9.12,20.11,22.62-.08,13.5-9.13,22.44-20.39,22.37Z"
          fillRule="evenodd"
          fill="url(#ergon-wordmark-pro-gradient)"
        />
      </g>
    </svg>
  );
}
